import { Component } from '@theme/component';

/** How far past the edge still counts as fitting, in pixels. Two cards are
 * 406.4 wide and the track is capped at 406.4, so sub-pixel rounding alone can
 * report a 1px overflow that nobody can scroll. */
const EDGE_TOLERANCE = 2;

/**
 * The two things the mega menu feature needs that CSS cannot do.
 *
 * The arrows. The track shows two pieces and scrolls the rest; the arrows only
 * exist while there is something to scroll to, and each end disables its own.
 *
 * Keeping the box on screen. The dropdown hangs from the left edge of its
 * link's text (Figma 416:5561 at x=539). CATEGORÍAS starts at 537.7 at every
 * desktop width, so a 750-wide box runs past the header's content edge below
 * 1408 and off the screen below 1288. The box is pulled left by exactly what
 * overflows, never further than the content's left edge.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement} [arrows]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class MmMenuFeatureComponent extends Component {
  requiredRefs = ['track'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // `scroll` does not bubble, but it does pass through ancestors on the
    // capture phase, so the listener survives the track being re-rendered.
    this.addEventListener('scroll', this.#updateArrows, { capture: true, passive: true });
    window.addEventListener('resize', this.#layout);

    this.#resizeObserver = new ResizeObserver(this.#layout);
    this.#resizeObserver.observe(this.refs.track);

    this.#layout();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#layout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('scroll', this.#updateArrows, { capture: true });
    window.removeEventListener('resize', this.#layout);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  previous() {
    this.#step(-1);
  }

  next() {
    this.#step(1);
  }

  get #isDrawer() {
    return this.classList.contains('mm-menu-feature--drawer');
  }

  /** @param {number} direction */
  #step(direction) {
    const { track } = this.refs;
    // Half the window lands on the next piece: scroll-snap does the rest.
    track.scrollBy({ left: (direction * track.clientWidth) / 2, behavior: 'smooth' });
  }

  #layout = () => {
    if (this.#isDrawer) return;
    this.#keepOnScreen();
    this.#updateArrows();
  };

  #keepOnScreen() {
    const submenu = this.closest('.menu-list__submenu');
    const columns = this.closest('.header__columns');
    if (!(submenu instanceof HTMLElement) || !(columns instanceof HTMLElement)) return;

    submenu.style.removeProperty('translate');

    const box = submenu.getBoundingClientRect();
    const bounds = columns.getBoundingClientRect();
    const style = getComputedStyle(columns);
    const right = bounds.right - parseFloat(style.paddingRight);
    const left = bounds.left + parseFloat(style.paddingLeft);

    const overflow = box.right - right;
    if (overflow <= 0) return;

    const shift = Math.min(overflow, Math.max(0, box.left - left));
    submenu.style.translate = `${-Math.ceil(shift)}px 0`;
  }

  #updateArrows = () => {
    const { track, arrows, previous, next } = this.refs;
    if (!arrows) return;

    const overflows = track.scrollWidth - track.clientWidth > EDGE_TOLERANCE;
    arrows.hidden = !overflows;
    if (!overflows) return;

    if (previous) previous.disabled = track.scrollLeft <= EDGE_TOLERANCE;
    if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - EDGE_TOLERANCE;
  };
}

if (!customElements.get('mm-menu-feature-component')) {
  customElements.define('mm-menu-feature-component', MmMenuFeatureComponent);
}
